import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate, fmtTime, fmtDateTime } from "@/lib/format";
import { Search, Loader2, FileText, Clock, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function History() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [driverId, setDriverId] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [timeline, setTimeline] = useState(null);

  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/drivers")).data });
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["history", q, status, driverId, from, to],
    queryFn: async () => (await api.get("/orders", { params: {
      q: q || undefined, status: status === "all" ? undefined : status,
      driver_id: driverId === "all" ? undefined : driverId,
      date_from: from || undefined, date_to: to || undefined,
    } })).data,
  });

  return (
    <div className="animate-slide-up">
      <h1 className="font-head text-2xl font-extrabold text-[#0B192C]">Delivery History</h1>
      <p className="text-sm text-slate-500">Searchable record of all deliveries</p>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="history-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order #, POD, customer…" className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-[#0B192C]" />
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-[160px] bg-white" data-testid="history-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{["Completed","Partial Delivery","Failed Delivery","In Transit","Arrived","Assigned","Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={driverId} onValueChange={setDriverId}><SelectTrigger className="w-[150px] bg-white" data-testid="history-driver"><SelectValue placeholder="Driver" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All drivers</SelectItem>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="history-from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="history-to" />
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white scrollbar-thin">
        {isLoading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FF6500]" /></div> : (
          <table className="w-full text-sm" data-testid="history-table">
            <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3">Order</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Driver</th>
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">On Site</th><th className="px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No records</td></tr>}
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`history-row-${o.order_number}`}>
                  <td className="px-4 py-3 font-mono font-bold text-[#FF6500]">#{o.order_number}</td>
                  <td className="px-4 py-3 font-semibold text-[#0B192C]">{o.customer_snapshot.name}</td>
                  <td className="px-4 py-3 text-slate-600">{o.driver_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(o.delivery_date, { day: "numeric", month: "short", year: "numeric" })}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-slate-500">{o.delivery?.time_on_site_minutes != null ? `${o.delivery.time_on_site_minutes} min` : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setTimeline(o)} data-testid={`timeline-${o.order_number}`} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" title="Timeline"><Clock size={16} /></button>
                      {o.pod_number && <button onClick={() => navigate(`/admin/pod/${o.id}`)} data-testid={`history-pod-${o.order_number}`} className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50" title="POD"><FileText size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {timeline && <TimelineDialog order={timeline} onClose={() => setTimeline(null)} />}
    </div>
  );
}

function TimelineDialog({ order, onClose }) {
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["audit", order.id],
    queryFn: async () => (await api.get(`/orders/${order.id}/audit`)).data,
  });
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="timeline-dialog">
        <DialogHeader><DialogTitle>Timeline · #{order.order_number}</DialogTitle></DialogHeader>
        {isLoading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#FF6500]" /></div> : (
          <ol className="relative ml-3 border-l-2 border-slate-200">
            {events.map((e) => (
              <li key={e.id} className="mb-5 ml-5">
                <span className="absolute -left-[9px] mt-1 h-4 w-4 rounded-full border-2 border-white bg-[#FF6500]" />
                <div className="text-sm font-bold text-[#0B192C]">{e.action}</div>
                <div className="text-xs text-slate-500">{fmtDateTime(e.timestamp)} {e.user_name && `· ${e.user_name}`}</div>
                {e.extra?.gps && <div className="text-xs text-slate-400">GPS: {e.extra.gps}</div>}
                {e.extra?.receiver && <div className="text-xs text-slate-400">Receiver: {e.extra.receiver}</div>}
                {e.extra?.driver && <div className="text-xs text-slate-400">Driver: {e.extra.driver}</div>}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
